import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../Context/TenantContext';
import { UserContext } from '../BasicComponents/UserContext';
import {
  Bars3Icon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

/**
 * TenantHeader
 *
 * Sticky top navigation bar for the student tenant dashboard.
 *
 * Left  - Mobile hamburger -> [college logo or fallback icon] -> college name -> "Student Portal"
 * Right - Student name -> avatar (profile pic -> initials fallback)
 *
 * Logo is always rendered: shows the tenant logo if available, falls back to
 * an AcademicCapIcon icon if logoUrl is absent or the image fails to load.
 * This guarantees the branding row is never empty or visually broken.
 */
const TenantHeader = ({ onMenuToggle }) => {
  const { getCollegeName, getLogoUrl, tenant } = useTenant();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const collegeName = getCollegeName();
  const logoUrlFromCtx = getLogoUrl();
  const collegeLogo = tenant?.logoUrl || logoUrlFromCtx;

  // Track whether the logo image failed to load so we can show the fallback.
  const [logoError, setLogoError] = useState(false);
  const logoUrl = logoError ? null : collegeLogo;

  // Student display name - prefer full name, fallback to username
  const studentName = user?.name || user?.first_name || user?.username || 'Student';

  // Initials for the avatar fallback (up to 2 letters)
  const initials = studentName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Profile image: prefer avatar chosen in CleanProfile, then backend-stored image
  const profileImage = (() => {
    try {
      return (
        localStorage.getItem('selectedAvatar') ||
        user?.profile_image ||
        user?.avatar_url ||
        null
      );
    } catch {
      return null;
    }
  })();

  const [avatarError, setAvatarError] = useState(false);
  const showProfilePic = profileImage && !avatarError;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40"
      style={{
        height: '61px',
        background: '#FFFFFF',
        borderBottom: '1px solid var(--border-subtle)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 28px',
      }}
    >
      {/* -- Left: mobile hamburger + branding -- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
        {/* Mobile hamburger */}
        <button
          onClick={onMenuToggle}
          className="md:hidden p-2 rounded-lg hover:bg-bgSoft active:scale-95 transition-colors flex-shrink-0"
          aria-label="Open navigation menu"
        >
          <Bars3Icon className="h-5 w-5 text-muted" />
        </button>

        {/* Branding (Desktop + Mobile) */}
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl && !logoError ? (
            <img
              src={logoUrl}
              alt={collegeName}
              style={{
                width: '34px',
                height: '34px',
                objectFit: 'contain',
                flexShrink: 0,
                border: '1px solid var(--border-subtle, #E5E7EB)',
                borderRadius: '8px',
                padding: '2px',
              }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: 'rgba(79, 70, 229,0.08)',
                border: '1.5px solid rgba(79, 70, 229,0.2)',
              }}
            >
              <AcademicCapIcon
                style={{ width: '20px', height: '20px', color: 'var(--brand-primary)' }}
              />
            </div>
          )}

          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                fontSize: '0.875rem',
                fontWeight: 800,
                color: 'var(--brand-primary)',
                letterSpacing: '-0.01em',
                lineHeight: 1,
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 'clamp(160px, 30vw, 360px)',
              }}
            >
              {collegeName}
            </h1>
            <p
              style={{
                fontSize: '0.625rem',
                color: 'var(--text-muted, #6B7280)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.09em',
                marginTop: '3px',
                lineHeight: 1,
              }}
            >
              Student Portal
            </p>
          </div>
        </div>
      </div>

      {/* -- Right: user identity pill -- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '5px 12px 5px 6px',
          borderRadius: '999px',
          background: 'rgba(79, 70, 229,0.05)',
          border: '1px solid rgba(79, 70, 229,0.14)',
          flexShrink: 0,
        }}
      >
        {/* Avatar / Initials */}
        <button
          onClick={() => navigate('/profile')}
          style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: showProfilePic ? '1.5px solid var(--brand-primary)' : 'none',
            background: showProfilePic ? 'white' : 'var(--brand-primary)',
            padding: 0,
            cursor: 'pointer'
          }}
        >
          {showProfilePic ? (
            <img
              src={profileImage}
              alt={studentName}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={() => setAvatarError(true)}
            />
          ) : (
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, color: '#fff' }}>
              {initials.charAt(0)}
            </span>
          )}
        </button>

        {/* Profile Name */}
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'var(--brand-primary)',
            letterSpacing: '0.01em',
            maxWidth: '140px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {studentName}
        </span>

        {/* Role label */}
        <span
          style={{
            fontSize: '0.6rem',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'rgba(79, 70, 229,0.65)',
            borderLeft: '1px solid rgba(79, 70, 229,0.2)',
            paddingLeft: '8px',
            lineHeight: 1,
          }}
        >
          Student
        </span>
      </div>
    </header>
  );
};

export default TenantHeader;

